import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class RequestDedupeService {
  private readonly inFlightRequests = new Map<string, Promise<unknown>>();

  // Cart quantity is sent as a delta (quantity: 1 means "one more"), so repeating the
  // identical payload is a legitimate second mutation, not a duplicate submit.
  private readonly ADDITIVE_API_METHODS = new Set<string>(['addProductToCart', 'removeProductFromCart']);

  // Regenerated on every call (uuid / ISO timestamp / timestamp signature), so keeping them
  // in the key would make a duplicate submit look like a distinct request.
  private readonly PER_CALL_PAYLOAD_FIELDS = new Set<string>(['uniqueId', 'time', 'digest']);

  private readonly MAX_IN_FLIGHT_MS = 30000;

  deduped<T>(apiName: string, payload: unknown, execute: () => Promise<T>): Promise<T> {
    const key = this.buildKey(apiName, payload);
    if (!key) return execute();

    const inFlight = this.inFlightRequests.get(key) as Promise<T> | undefined;
    if (inFlight) return inFlight;

    const request = execute();
    this.retainInFlight(key, request);
    return request;
  }

  wrap<T extends object>(api: T): T {
    const wrappedNames = new Set<string>();
    let level: object | null = Object.getPrototypeOf(api);
    while (level && level !== Object.prototype) {
      for (const name of Object.getOwnPropertyNames(level)) {
        if (name === 'constructor' || wrappedNames.has(name)) continue;
        const descriptor = Object.getOwnPropertyDescriptor(level, name);
        if (!descriptor || typeof descriptor.value !== 'function') continue;
        wrappedNames.add(name);
        const original = descriptor.value as (...args: unknown[]) => unknown;
        Object.defineProperty(api, name, {
          configurable: true,
          writable: true,
          enumerable: false,
          value: (...args: unknown[]): unknown => this.callDeduped(api, name, original, args),
        });
      }
      level = Object.getPrototypeOf(level);
    }
    return api;
  }

  private callDeduped<T extends object>(api: T, name: string, original: (...args: unknown[]) => unknown,
    args: unknown[]): unknown {
    const key = this.buildKey(name, args);
    if (!key) return original.apply(api, args);

    const inFlight = this.inFlightRequests.get(key);
    if (inFlight) return inFlight;

    const result = original.apply(api, args);
    if (result instanceof Promise) this.retainInFlight(key, result);
    return result;
  }

  private buildKey(apiName: string, payload: unknown): string | null {
    if (this.ADDITIVE_API_METHODS.has(apiName)) return null;
    try {
      const stableFields = (field: string, value: unknown): unknown => (
        this.PER_CALL_PAYLOAD_FIELDS.has(field) ? undefined : value);
      return `${apiName}:${JSON.stringify(payload, stableFields) ?? ''}`;
    } catch {
      return null;
    }
  }

  private retainInFlight(key: string, request: Promise<unknown>): void {
    this.inFlightRequests.set(key, request);
    const expiry = setTimeout((): void => {
      this.inFlightRequests.delete(key);
    }, this.MAX_IN_FLIGHT_MS);
    const settle = (): void => {
      clearTimeout(expiry);
      this.inFlightRequests.delete(key);
    };
    request.then(settle, settle);
  }
}
